import React, { useEffect, useRef, useState } from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  PieChart, Pie, Cell, Legend,
} from "recharts";

const PALETTE = [
  "#D47A65", "#94A68D", "#264D4F", "#C7A76F",
  "#4A6E75", "#B85F4A", "#7A9078", "#3E7280",
];

/** Measures parent width using ResizeObserver — reliable across all layouts including flex, grid, and PDF export. */
function useContainerWidth() {
  const ref = useRef(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    if (!ref.current) return;
    const el = ref.current;
    const measure = () => setWidth(el.offsetWidth || 0);
    measure();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, width];
}

/**
 * ResultChart — responsive graphical display for dominant-dimension scoring.
 * Uses a measured width from parent (avoids Recharts 3.x ResponsiveContainer zero-width edge cases).
 */
export default function ResultChart({ dimensions = [], type = "bar_horizontal", lang = "id", showTopOnly = false, topN = 3 }) {
  const [wrapRef, width] = useContainerWidth();
  if (!dimensions.length) return <div ref={wrapRef} className="w-full" style={{ height: 220 }} />;
  const source = showTopOnly ? [...dimensions].sort((a, b) => (b.percentage || 0) - (a.percentage || 0)).slice(0, topN) : dimensions;
  const data = source.map((d, i) => ({
    name: (lang === "en" ? d.name_en : d.name_id) || d.code || `Dim ${i + 1}`,
    code: d.code || `D${i + 1}`,
    percentage: Number((d.percentage ?? 0).toFixed(1)),
    raw: Number((d.raw_score ?? 0).toFixed(1)),
    max: Number((d.max_possible ?? 0).toFixed(1)),
    fill: PALETTE[i % PALETTE.length],
  }));

  const tooltipFormatter = (val, key, ctx) => {
    if (key === "percentage") return [`${val}%`, lang === "en" ? "Percentage" : "Persentase"];
    if (key === "raw") return [`${val} / ${ctx.payload.max}`, lang === "en" ? "Score" : "Skor"];
    return [val, key];
  };

  const heightHorizontal = Math.max(220, data.length * 48);
  const chartW = Math.max(200, width || 320);

  return (
    <div ref={wrapRef} className="w-full">
      {width > 0 && type === "bar_horizontal" && (
        <BarChart width={chartW} height={heightHorizontal} data={data} layout="vertical" margin={{ top: 8, right: 24, bottom: 8, left: 8 }}>
          <CartesianGrid horizontal={false} stroke="hsl(var(--border))" strokeDasharray="3 3" />
          <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} unit="%" />
          <YAxis type="category" dataKey="name" tick={{ fontSize: 12, fill: "hsl(var(--foreground))" }} width={110} />
          <Tooltip formatter={tooltipFormatter} contentStyle={{ borderRadius: 8, border: "1px solid hsl(var(--border))" }} />
          <Bar dataKey="percentage" radius={[0, 6, 6, 0]}>
            {data.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
          </Bar>
        </BarChart>
      )}
      {width > 0 && type === "bar_vertical" && (
        <BarChart width={chartW} height={300} data={data} margin={{ top: 16, right: 8, bottom: 8, left: 0 }}>
          <CartesianGrid vertical={false} stroke="hsl(var(--border))" strokeDasharray="3 3" />
          <XAxis dataKey="name" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} interval={0} angle={-15} textAnchor="end" height={60} />
          <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} unit="%" />
          <Tooltip formatter={tooltipFormatter} contentStyle={{ borderRadius: 8, border: "1px solid hsl(var(--border))" }} />
          <Bar dataKey="percentage" radius={[6, 6, 0, 0]}>
            {data.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
          </Bar>
        </BarChart>
      )}
      {width > 0 && type === "radar" && (
        <RadarChart width={chartW} height={320} data={data} outerRadius="75%">
          <PolarGrid stroke="hsl(var(--border))" />
          <PolarAngleAxis dataKey="name" tick={{ fontSize: 11, fill: "hsl(var(--foreground))" }} />
          <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} />
          <Radar name={lang === "en" ? "Percentage" : "Persentase"} dataKey="percentage" stroke="#D47A65" fill="#D47A65" fillOpacity={0.35} />
          <Tooltip formatter={tooltipFormatter} contentStyle={{ borderRadius: 8, border: "1px solid hsl(var(--border))" }} />
        </RadarChart>
      )}
      {width > 0 && (type === "pie" || type === "donut") && (
        <PieChart width={chartW} height={320}>
          <Pie
            data={data}
            dataKey="percentage"
            nameKey="name"
            cx="50%"
            cy="50%"
            outerRadius="80%"
            innerRadius={type === "donut" ? 60 : 0}
            paddingAngle={type === "donut" ? 2 : 0}
            label={(entry) => `${entry.percentage}%`}
            labelLine={false}
          >
            {data.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
          </Pie>
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Tooltip formatter={(v, k, ctx) => [`${v}%`, ctx.payload.name]} contentStyle={{ borderRadius: 8, border: "1px solid hsl(var(--border))" }} />
        </PieChart>
      )}
    </div>
  );
}
